import { t } from 'i18next';

import {
  CurrentPlanResponse,
  StudioPlan,
  StudioPlanChangeInterval,
  StudioPlanChangePreviewRequest,
  StudioPlanId,
  StudioPlanTier,
} from 'src/services/api/studioCreditApi';

import {
  EMPTY_VALUE,
  getStudioPlanTitle,
  STUDIO_PLAN_RANK,
} from './studioFeatureGating';

export type SellableStudioPlanId = Exclude<StudioPlanId, 'freemium'>;

export type StudioBillingInterval = 'month' | 'year';

/** Base (lowest) tier per sellable plan — the "starting at" price of each. */
export type StudioBaseTiers = Map<SellableStudioPlanId, StudioPlanTier>;

/**
 * Plan pricing handed to the upgrade modal. The owner of the query resolves it,
 * so the modal renders one of these states without fetching anything itself.
 */
export type StudioPlanPricingState =
  | { status: 'loading' }
  | { status: 'error'; onRetry: () => void }
  | { status: 'ready'; baseTiers: StudioBaseTiers };

export function getBaseTiersByPlan(plans: StudioPlan[]): StudioBaseTiers {
  const baseTiers: StudioBaseTiers = new Map();
  plans.forEach((plan) => {
    if (plan.plan === 'freemium') return;
    const baseTier = plan.tiers.at(0);
    if (baseTier) baseTiers.set(plan.plan, baseTier);
  });
  return baseTiers;
}

// Annual prices are stored per year with the discount baked in; the "starting
// at" figures render as an equivalent per-month price.
export function monthlyPriceCents(
  tier: StudioPlanTier,
  interval: StudioBillingInterval,
): number {
  if (interval === 'year' && tier.annualCents !== undefined) {
    return tier.annualCents / 12;
  }
  return tier.monthlyCents;
}

/**
 * A plan-change target: the plan, credit total, and interval. Grid cards only
 * produce sellable plans; the manage-plan downgrade path can target freemium.
 */
export interface StudioPlanSelection {
  planId: StudioPlanId;
  planTitle: string;
  interval: StudioBillingInterval;
  tier: {
    credits: number;
    monthlyCents: number;
    annualCents?: number;
    /** The target plan's included allowance; anchors the modal's dropdown. */
    includedCredits?: number;
  };
}

/** Maps the page's month/year toggle onto the catalog's canonical intervals. */
export const toApiInterval = (
  interval: StudioBillingInterval,
): StudioPlanChangeInterval => (interval === 'year' ? 'annual' : 'monthly');

/**
 * Maps a subscription's interval string onto the page's month/year toggle;
 * anything other than the annual marker reads as monthly.
 */
export const fromApiInterval = (interval?: string): StudioBillingInterval =>
  interval === 'annual' ? 'year' : 'month';

/**
 * Whether a plan card under the given toggle tab IS the current subscription:
 * plan and billing interval must both match, so an annual subscriber browsing
 * the monthly tab sees a regular change target, not their current plan. The
 * credit total is a variant picker inside the card, not part of its identity.
 */
export const isCurrentPlanSelection = (
  planId: StudioPlanId,
  interval: StudioBillingInterval,
  currentPlan?: CurrentPlanResponse,
): boolean =>
  isResolvedStudioPlan(currentPlan) &&
  currentPlan.plan === planId &&
  fromApiInterval(currentPlan.interval) === interval;

/**
 * Grid visibility per the pricing-table design: the grid shows only the
 * current plan and higher tiers — lower plans are hidden (the downgrade
 * entry point is the manage-plan flow, not the grid). While the current plan
 * is unresolved every card stays visible (and disabled), preserving the
 * loading layout.
 */
export const isStudioPlanCardVisible = (
  planId: SellableStudioPlanId,
  currentPlan?: CurrentPlanResponse,
): boolean =>
  !isResolvedStudioPlan(currentPlan) ||
  STUDIO_PLAN_RANK[planId] >= STUDIO_PLAN_RANK[currentPlan.plan];

/**
 * Whether the current plan's card CTA has anything to submit: only a credit
 * total strictly above the subscription's current total is an increase.
 */
export const isCreditIncrease = (
  selectedCredits: number,
  currentPlan?: CurrentPlanResponse,
): boolean => getCreditChangeKind(selectedCredits, currentPlan) === 'increase';

export type CreditChangeKind = 'increase' | 'decrease';

/**
 * How a selected total relates to the subscribed one: an increase applies
 * immediately, a decrease is give-less and schedules at renewal, and equal
 * (or an unresolved plan) is nothing to submit.
 */
export const getCreditChangeKind = (
  selectedCredits: number,
  currentPlan?: CurrentPlanResponse,
): CreditChangeKind | undefined => {
  if (!isResolvedStudioPlan(currentPlan) || currentPlan.credits === undefined) {
    return undefined;
  }
  // Annual subscribers can't self-serve credit changes yet (backend gate).
  if (isAnnualStudioSubscription(currentPlan)) {
    return undefined;
  }
  if (selectedCredits > currentPlan.credits) {
    return 'increase';
  }
  return selectedCredits < currentPlan.credits ? 'decrease' : undefined;
};

const getPlanAtRank = (rank: number): StudioPlanId | undefined =>
  (Object.keys(STUDIO_PLAN_RANK) as StudioPlanId[]).find(
    (candidate) => STUDIO_PLAN_RANK[candidate] === rank,
  );

/** The plan one tier below, or undefined at the bottom of the ladder. */
export const getPlanOneTierBelow = (
  plan: StudioPlanId,
): StudioPlanId | undefined => getPlanAtRank(STUDIO_PLAN_RANK[plan] - 1);

/** The plan one tier above, or undefined at the top of the ladder. */
export const getPlanOneTierAbove = (
  plan: StudioPlanId,
): StudioPlanId | undefined => getPlanAtRank(STUDIO_PLAN_RANK[plan] + 1);

/**
 * Whether the downgrade-to-Free flow detours through the retention offer:
 * only Professional and Advanced have a paid tier below to switch to —
 * Starter goes straight to the reason survey.
 */
export const isRetentionEligible = (
  currentPlan?: CurrentPlanResponse,
): boolean =>
  isResolvedStudioPlan(currentPlan) &&
  STUDIO_PLAN_RANK[currentPlan.plan] >= STUDIO_PLAN_RANK.professional;

/**
 * Builds a change target for a catalog plan at its included allowance:
 * freemium is only sold monthly, other targets keep the current billing
 * interval. Undefined when the catalog doesn't carry the plan.
 */
export const buildPlanChangeSelection = (
  targetPlan: StudioPlanId,
  currentPlan: CurrentPlanResponse | undefined,
  plans: StudioPlan[] | undefined,
): StudioPlanSelection | undefined => {
  const tier = plans?.find((plan) => plan.plan === targetPlan)?.tiers.at(0);
  if (!tier) {
    return undefined;
  }
  return {
    planId: targetPlan,
    planTitle: getStudioPlanTitle(targetPlan),
    interval:
      targetPlan === 'freemium'
        ? 'month'
        : fromApiInterval(currentPlan?.interval),
    tier: {
      credits: tier.credits,
      monthlyCents: tier.monthlyCents,
      annualCents: tier.annualCents,
      includedCredits: tier.credits,
    },
  };
};

// Credits are sold as a quantity item on top of the plan's included
// allowance, so the catalog carries a single tier per plan and the selector
// synthesizes the quantity options client-side. These are the PRD matrix
// steps, capped at the 10,000 monthly-credit product maximum.
const EXTRA_CREDIT_LADDER = [
  300, 400, 600, 800, 1000, 1500, 2000, 4000, 6000, 8000, 10000,
];

/**
 * The credit totals a plan card offers: the plan's included allowance plus
 * every ladder step above it.
 */
export const getCreditQuantityOptions = (includedCredits: number): number[] => [
  includedCredits,
  ...EXTRA_CREDIT_LADDER.filter((credits) => credits > includedCredits),
];

/**
 * The selector options for a plan the workspace is subscribed to: the ladder
 * plus the subscribed total injected in order when API-side changes put it
 * off-ladder, so the current selection can always render.
 */
export const getCreditSelectorOptions = (
  includedCredits: number,
  subscribedTotal?: number,
): number[] => {
  const options = getCreditQuantityOptions(includedCredits);
  if (subscribedTotal === undefined || options.includes(subscribedTotal)) {
    return options;
  }
  return [...options, subscribedTotal].sort((a, b) => a - b);
};

/**
 * The upgrade modal's dropdown speaks in ADDITIONAL credits above the target
 * plan's included allowance, while the change request wants the TOTAL — one
 * option carries both.
 */
export interface AdditionalCreditOption {
  totalCredits: number;
  additionalCredits: number;
}

export const getAdditionalCreditOptions = (
  includedCredits: number,
  subscribedTotal?: number,
): AdditionalCreditOption[] =>
  getCreditSelectorOptions(includedCredits, subscribedTotal).map(
    (totalCredits) => ({
      totalCredits,
      additionalCredits: totalCredits - includedCredits,
    }),
  );

/**
 * PRD extra-credit rate: $0.60 per credit per month. Copy/derivation only —
 * the authoritative charge always comes from the preview endpoint.
 */
export const EXTRA_CREDIT_MONTHLY_CENTS_PER_CREDIT = 60;

/** The recurring monthly total after adding extras at the PRD rate. */
export const getMonthlyTotalWithExtrasCents = (
  baseMonthlyCents: number,
  additionalCredits: number,
): number =>
  baseMonthlyCents + additionalCredits * EXTRA_CREDIT_MONTHLY_CENTS_PER_CREDIT;

/** The upgrade modal's due-today preview request for a selection. */
export const toPlanChangePreviewRequest = (
  selection: StudioPlanSelection,
  totalCredits: number,
): StudioPlanChangePreviewRequest => ({
  plan: selection.planId,
  interval: toApiInterval(selection.interval),
  credits: totalCredits,
});

export type PlanSelectionKind = 'upgrade' | 'downgrade' | 'intervalChange';

/**
 * A current plan the change flows can classify against: a resolved Studio
 * subscription. Anything else (still loading, Classic, none) has no
 * self-serve change flow in this page, so the CTAs gate on this.
 */
export const isResolvedStudioPlan = (
  currentPlan?: CurrentPlanResponse,
): currentPlan is CurrentPlanResponse & { plan: StudioPlanId } =>
  currentPlan?.family === 'studio' && currentPlan.plan !== undefined;

/**
 * Annual subscriptions cannot self-serve ANY plan or credit change yet — the
 * backend rejects every one with `studio_plan_change_annual_unsupported`
 * (annual support is deferred to POR-21399). The frontend mirrors that gate
 * exactly, so no annual-source change is ever presented as actionable.
 */
export const isAnnualStudioSubscription = (
  currentPlan?: CurrentPlanResponse,
): boolean =>
  isResolvedStudioPlan(currentPlan) && currentPlan.interval === 'annual';

/** Interval axis rank, mirroring the backend classifier: monthly < annual. */
const STUDIO_INTERVAL_RANK: Record<StudioPlanChangeInterval, number> = {
  monthly: 0,
  annual: 1,
};

// The current plan's interval is a plain string; anything other than the
// annual marker ranks as monthly.
const intervalRank = (interval?: string): number =>
  interval === 'annual'
    ? STUDIO_INTERVAL_RANK.annual
    : STUDIO_INTERVAL_RANK.monthly;

/**
 * Classifies a plan-card choice against the current plan, or undefined when
 * the current plan is not a resolved Studio subscription (no blind requests)
 * or the selection changes nothing.
 *
 * This mirrors backend plans.Classify exactly. Three ranked axes — plan rank,
 * total credits, interval rank (monthly < annual) — compared target vs
 * current:
 * - every axis >= current with at least one strictly greater is an UPGRADE;
 * - every axis <= current with at least one strictly lower is a DOWNGRADE;
 * - a mix of up and down axes is a DOWNGRADE (the backend's fail-safe);
 * - all equal is NO CHANGE.
 * Downgrades (pure or mixed) route to the survey flow — the downgrade
 * endpoint accepts them regardless of interval direction (including
 * annual→monthly and annual give-less staying annual — ScheduleDowngrade has
 * no annual gate). A pure give-more opens the upgrade modal only when the
 * TARGET interval is monthly; a give-more whose target interval is annual
 * (monthly→annual, or annual→annual give-more) short-circuits to the
 * 'intervalChange' gate because the upgrade endpoint rejects target-annual.
 */
export const classifyPlanSelection = (
  selection: StudioPlanSelection,
  currentPlan?: CurrentPlanResponse,
): PlanSelectionKind | undefined => {
  if (!isResolvedStudioPlan(currentPlan)) {
    return undefined;
  }
  const intervalDelta =
    intervalRank(toApiInterval(selection.interval)) -
    intervalRank(currentPlan.interval);
  const axisDeltas = [
    STUDIO_PLAN_RANK[selection.planId] - STUDIO_PLAN_RANK[currentPlan.plan],
    // An unknown current credit total compares as equal on that axis.
    currentPlan.credits !== undefined
      ? selection.tier.credits - currentPlan.credits
      : 0,
    intervalDelta,
  ];
  const someAxisDown = axisDeltas.some((delta) => delta < 0);
  const someAxisUp = axisDeltas.some((delta) => delta > 0);
  if (someAxisDown) {
    return 'downgrade';
  }
  if (!someAxisUp) {
    return undefined;
  }
  // Pure give-more: the upgrade endpoint rejects any target-annual upgrade
  // (monthly→annual AND annual→annual give-more), so gate those; a
  // monthly-target give-more is a normal upgrade.
  return toApiInterval(selection.interval) === 'annual'
    ? 'intervalChange'
    : 'upgrade';
};

/** The CTA a grid plan card renders, derived from its classified change. */
export type StudioPlanCardCta =
  | 'upgrade'
  | 'downgrade'
  | 'upgradeCurrent'
  | 'downgradeCurrent';

/**
 * The grid plan-card CTA for a selection. The current-plan card submits a
 * credits-only change (increase → upgradeCurrent, decrease → downgradeCurrent);
 * every other card's label follows the same classification that routes its
 * click, so an annual→monthly (give-less) selection reads "downgrade" and
 * matches the survey it opens. A gated give-more ('intervalChange') keeps the
 * neutral upgrade label — the click is intercepted with an affordance.
 */
export const getStudioPlanCardCta = (
  selection: StudioPlanSelection,
  currentPlan: CurrentPlanResponse | undefined,
  isCurrentPlan: boolean,
  creditChangeKind: CreditChangeKind | undefined,
): StudioPlanCardCta => {
  if (isCurrentPlan) {
    return creditChangeKind === 'decrease'
      ? 'downgradeCurrent'
      : 'upgradeCurrent';
  }
  return classifyPlanSelection(selection, currentPlan) === 'downgrade'
    ? 'downgrade'
    : 'upgrade';
};

/**
 * Renders a billing instant (renewal date, downgrade effective date) as a
 * long date. Billing instants are midnight-UTC anchors, so they render in UTC
 * lest a UTC-negative browser show the previous calendar day.
 */
export const formatStudioPlanDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export interface StudioFeatureItem {
  label: string;
  badge?: string;
}

export interface StudioFeatureGroup {
  title: string;
  items: StudioFeatureItem[];
}

export interface StudioPlanDisplay {
  id: SellableStudioPlanId;
  title: string;
  subTitle: string;
  featureGroups: StudioFeatureGroup[];
}

// PRD included allowance for every paid Studio plan — the i18n fallback when
// the catalog hasn't loaded; the API value wins so enterprise per-portal
// overrides display correctly.
const PRD_INCLUDED_CREDITS = 200;

/**
 * Renders the plan-card bullet lists. `plans` (the /studio-plans catalog)
 * supplies each plan's included credits; absent entries fall back to the PRD
 * number.
 */
export function getStudioPlanDisplays(
  plans?: StudioPlan[],
): StudioPlanDisplay[] {
  const includedCredits = (planId: SellableStudioPlanId): number =>
    plans?.find((plan) => plan.plan === planId)?.tiers.at(0)?.credits ??
    PRD_INCLUDED_CREDITS;

  return [
    {
      id: 'starter',
      title: t(($) => $.page_studio_plans.tiers.starter.title),
      subTitle: t(($) => $.page_studio_plans.tiers.starter.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.usage),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.starter),
            },
            {
              label: t(($) => $.page_studio_plans.feat.active_clients.starter),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.app_builder),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('starter'),
              }),
            },
            { label: t(($) => $.page_studio_plans.feat.included_apps) },
            { label: t(($) => $.page_studio_plans.feat.extra_apps) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.features),
          items: [
            { label: t(($) => $.page_studio_plans.feat.custom_domain) },
            { label: t(($) => $.page_studio_plans.feat.api_integrations) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [
            { label: t(($) => $.page_studio_plans.feat.standard_support) },
          ],
        },
      ],
    },
    {
      id: 'professional',
      title: t(($) => $.page_studio_plans.tiers.pro.title),
      subTitle: t(($) => $.page_studio_plans.tiers.pro.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.usage),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.pro),
              badge: t(($) => $.page_studio_plans.feat.per_user_badge.pro),
            },
            {
              label: t(($) => $.page_studio_plans.feat.active_clients.pro),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.app_builder),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('professional'),
              }),
            },
            { label: t(($) => $.page_studio_plans.feat.included_apps) },
            { label: t(($) => $.page_studio_plans.feat.extra_apps) },
          ],
        },
        {
          title: t(
            ($) => $.page_studio_plans.sections.everything_in_starter_plus,
          ),
          items: [
            { label: t(($) => $.page_studio_plans.feat.multi_company_clients) },
            { label: t(($) => $.page_studio_plans.feat.automations) },
            { label: t(($) => $.page_studio_plans.feat.remove_badge) },
            { label: t(($) => $.page_studio_plans.feat.model_selection) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [
            { label: t(($) => $.page_studio_plans.feat.priority_support) },
          ],
        },
      ],
    },
    {
      id: 'advanced',
      title: t(($) => $.page_studio_plans.tiers.advanced.title),
      subTitle: t(($) => $.page_studio_plans.tiers.advanced.subTitle),
      featureGroups: [
        {
          title: t(($) => $.page_studio_plans.sections.usage),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.internal_users.advanced),
              badge: t(($) => $.page_studio_plans.feat.per_user_badge.advanced),
            },
            {
              label: t(($) => $.page_studio_plans.feat.active_clients.advanced),
            },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.app_builder),
          items: [
            {
              label: t(($) => $.page_studio_plans.feat.build_credits, {
                count: includedCredits('advanced'),
              }),
            },
            { label: t(($) => $.page_studio_plans.feat.included_apps) },
            { label: t(($) => $.page_studio_plans.feat.extra_apps) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.everything_in_pro_plus),
          items: [
            { label: t(($) => $.page_studio_plans.feat.audit_log) },
            {
              label: t(
                ($) => $.page_studio_plans.feat.client_access_permissions,
              ),
            },
            { label: t(($) => $.page_studio_plans.feat.enforce_mfa) },
          ],
        },
        {
          title: t(($) => $.page_studio_plans.sections.support),
          items: [
            { label: t(($) => $.page_studio_plans.feat.priority_support) },
            {
              label: t(($) => $.page_studio_plans.feat.personalized_onboarding),
            },
          ],
        },
      ],
    },
  ];
}

/**
 * Formats a cents amount as a dollar string, dropping the decimals when the
 * amount is a whole dollar figure (e.g. 5900 -> "$59", 374250 -> "$3,742.50").
 */
export function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
